import type { BreathingPattern, GoalConfig } from './Scene/gameLogic';

export interface BuddhaLevelConfig {
  id: number;
  label: string;
  backgroundVideoUrl: string;
  breathingPattern: BreathingPattern;
  penaltyMultiplier: number;
  voiceAudioEnabled: boolean;
  strictHoldTiming: boolean;
  /** Multiplies the base timing window ratio for inhale/exhale input. */
  inputWindowRatioMultiplier: number;
  /** When false, the in-scene breathing ring is not shown/updated (Level 3). */
  showBreathRing: boolean;
  /** When false, UI breathing guidance (phase label + instructions) is hidden (Level 3). */
  showBreathingOverlay: boolean;
  goalOverrides?: Partial<GoalConfig>;
}

export const LEVEL_CONFIGS: BuddhaLevelConfig[] = [
  {
    id: 1,
    label: 'Calm Rise',
    backgroundVideoUrl:
      'https://hsfbmpaclvbltdfaazpo.supabase.co/storage/v1/object/public/buddha_game/buddha-background-level-1.mp4',
    breathingPattern: {
      inhale: 4,
      holdAfterInhale: 0,
      exhale: 4,
      holdAfterExhale: 0,
    },
    penaltyMultiplier: 1,
    voiceAudioEnabled: true,
    strictHoldTiming: false,
    // More forgiving timing window on Level 1
    inputWindowRatioMultiplier: 1.2,
    showBreathRing: true,
    showBreathingOverlay: true,
  },
  {
    id: 2,
    label: 'Silent Focus',
    backgroundVideoUrl:
      'https://hsfbmpaclvbltdfaazpo.supabase.co/storage/v1/object/public/buddha_game/buddha-background-level-2.mp4',
    breathingPattern: {
      inhale: 4,
      holdAfterInhale: 0,
      exhale: 8,
      holdAfterExhale: 0,
    },
    penaltyMultiplier: 1.75,
    voiceAudioEnabled: false,
    strictHoldTiming: true,
    // Tighter timing window on Level 2
    inputWindowRatioMultiplier: 0.8,
    showBreathRing: true,
    showBreathingOverlay: true,
  },
  {
    id: 3,
    label: 'Mastery',
    backgroundVideoUrl:
      'https://hsfbmpaclvbltdfaazpo.supabase.co/storage/v1/object/public/buddha_game/buddha-background-level-3.mp4',
    breathingPattern: {
      inhale: 8,
      holdAfterInhale: 0,
      exhale: 8,
      holdAfterExhale: 0,
    },
    // Level 3 is at least as strict as Level 2: stronger penalties, tighter window.
    penaltyMultiplier: 2.25,
    voiceAudioEnabled: false,
    strictHoldTiming: true,
    inputWindowRatioMultiplier: 0.7,
    // Audio-only mastery: remove the ring and any UI breathing guidance.
    showBreathRing: false,
    showBreathingOverlay: false,
    // No streak-based forgiveness/boost on Level 3.
    goalOverrides: {
      streakBonusPerSuccess: 0,
      maxStreakBonus: 0,
    },
  },
];

export const DEFAULT_LEVEL_ID = 1;

export function getLevelConfig(levelId: number): BuddhaLevelConfig {
  return (
    LEVEL_CONFIGS.find((level) => level.id === levelId) ?? LEVEL_CONFIGS[0]!
  );
}
